import React, { useState } from 'react';
import useSimulationStore from '../../store/simulationStore';
import { shallow } from 'zustand/shallow';

const ControlPanel: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<'simulation' | 'view' | 'style' | 'chemistry'>('simulation');
  
  const {
    // Simulation Controls
    controls,
    isRunning,
    startSimulation,
    pauseSimulation,
    resetSimulation,
    setControls,
    
    // View Settings
    cameraFov,
    backgroundColor,
    ambientLightIntensity,
    viewportScaleFactor,
    activeVisShader,
    setCameraFov,
    setBackgroundColor,
    setAmbientLightIntensity,
    setViewportScaleFactor,
    setActiveVisShader,
    
    // Style Settings
    metalness,
    roughness,
    emissiveIntensity,
    bloomIntensity,
    bloomThreshold,
    bloomRadius,
    boxStyle,
    setMetalness,
    setRoughness,
    setEmissiveIntensity,
    setBloomIntensity,
    setBloomThreshold,
    setBloomRadius,
    setBoxStyle,
    
    // Chemistry Settings
    availablePresets,
    activeChemistryConfig,
    isChemistryConfigReady,
    loadPresetIntoActiveConfig
  } = useSimulationStore(state => ({
    // Simulation Controls
    controls: state.controls,
    isRunning: state.isRunning,
    startSimulation: state.startSimulation,
    pauseSimulation: state.pauseSimulation,
    resetSimulation: state.resetSimulation,
    setControls: state.setControls,
    
    // View Settings
    cameraFov: state.cameraFov,
    backgroundColor: state.backgroundColor,
    ambientLightIntensity: state.ambientLightIntensity,
    viewportScaleFactor: state.viewportScaleFactor,
    activeVisShader: state.activeVisShader,
    setCameraFov: state.setCameraFov,
    setBackgroundColor: state.setBackgroundColor,
    setAmbientLightIntensity: state.setAmbientLightIntensity,
    setViewportScaleFactor: state.setViewportScaleFactor,
    setActiveVisShader: state.setActiveVisShader,
    
    // Style Settings
    metalness: state.metalness,
    roughness: state.roughness,
    emissiveIntensity: state.emissiveIntensity,
    bloomIntensity: state.bloomIntensity,
    bloomThreshold: state.bloomThreshold,
    bloomRadius: state.bloomRadius,
    boxStyle: state.boxStyle,
    setMetalness: state.setMetalness,
    setRoughness: state.setRoughness,
    setEmissiveIntensity: state.setEmissiveIntensity,
    setBloomIntensity: state.setBloomIntensity,
    setBloomThreshold: state.setBloomThreshold,
    setBloomRadius: state.setBloomRadius,
    setBoxStyle: state.setBoxStyle,
    
    // Chemistry Settings
    availablePresets: state.availablePresets,
    activeChemistryConfig: state.activeChemistryConfig,
    isChemistryConfigReady: state.isChemistryConfigReady,
    loadPresetIntoActiveConfig: state.loadPresetIntoActiveConfig
  }), shallow);

  const toggleExpansion = () => {
    setIsExpanded(!isExpanded);
  };

  const handleSimulationControl = (key: string, value: any) => {
    setControls({ [key]: value });
  };

  const handleViewControl = (key: string, value: any) => {
    switch (key) {
      case 'cameraFov':
        setCameraFov(value);
        break;
      case 'backgroundColor':
        setBackgroundColor(value);
        break;
      case 'ambientLightIntensity':
        setAmbientLightIntensity(value);
        break;
      case 'viewportScaleFactor':
        setViewportScaleFactor(value);
        break;
      case 'activeVisShader':
        setActiveVisShader(value);
        break;
    }
  };

  const handleStyleControl = (key: string, value: any) => {
    switch (key) {
      case 'metalness':
        setMetalness(value);
        break;
      case 'roughness':
        setRoughness(value);
        break;
      case 'emissiveIntensity':
        setEmissiveIntensity(value);
        break;
      case 'bloomIntensity':
        setBloomIntensity(value);
        break;
      case 'bloomThreshold':
        setBloomThreshold(value);
        break;
      case 'bloomRadius':
        setBloomRadius(value);
        break;
      case 'boxStyle':
        setBoxStyle(value);
        break;
    }
  };

  const handlePresetChange = async (presetFile: string) => {
    if (presetFile) {
      try {
        await loadPresetIntoActiveConfig(presetFile);
      } catch (error) {
        console.error('Failed to load preset:', error);
      }
    }
  };

  return (
    <div style={containerStyle}>
      <div style={headerStyle} onClick={toggleExpansion}>
        <span>Controls</span>
        <button style={buttonStyle}>{isExpanded ? '-' : '+'}</button>
      </div>

      {isExpanded && (
        <div style={contentStyle}>
          {/* Tab Navigation */}
          <div style={tabContainerStyle}>
            <button
              style={{
                ...tabButtonStyle,
                ...(activeTab === 'simulation' ? activeTabStyle : {})
              }}
              onClick={() => setActiveTab('simulation')}
            >
              Simulation
            </button>
            <button
              style={{
                ...tabButtonStyle,
                ...(activeTab === 'view' ? activeTabStyle : {})
              }}
              onClick={() => setActiveTab('view')}
            >
              View
            </button>
            <button
              style={{
                ...tabButtonStyle,
                ...(activeTab === 'style' ? activeTabStyle : {})
              }}
              onClick={() => setActiveTab('style')}
            >
              Style
            </button>
            <button
              style={{
                ...tabButtonStyle,
                ...(activeTab === 'chemistry' ? activeTabStyle : {})
              }}
              onClick={() => setActiveTab('chemistry')}
            >
              Chemistry
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'simulation' && (
            <div style={tabContentStyle}>
              <div style={sectionStyle}>
                <h4 style={sectionTitleStyle}>Simulation Controls</h4>
                
                <div style={controlGroupStyle}>
                  <button
                    style={{
                      ...actionButtonStyle,
                      ...(isRunning ? pauseButtonStyle : startButtonStyle)
                    }}
                    onClick={isRunning ? pauseSimulation : startSimulation}
                  >
                    {isRunning ? '⏸️ Pause' : '▶️ Start'}
                  </button>
                  
                  <button
                    style={resetButtonStyle}
                    onClick={resetSimulation}
                  >
                    🔄 Reset
                  </button>
                </div>

                <div style={controlGroupStyle}>
                  <label style={labelStyle}>Reactor Size: {controls.reactorSize}</label>
                  <input
                    type="range"
                    min="10"
                    max="150"
                    step="10"
                    value={controls.reactorSize}
                    onChange={(e) => handleSimulationControl('reactorSize', parseInt(e.target.value))}
                    style={sliderStyle}
                  />
                </div>

                <div style={controlGroupStyle}>
                  <label style={labelStyle}>Simulation Speed: {controls.simulationSpeed.toFixed(2)}</label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={controls.simulationSpeed}
                    onChange={(e) => handleSimulationControl('simulationSpeed', parseFloat(e.target.value))}
                    style={sliderStyle}
                  />
                </div>

                <div style={controlGroupStyle}>
                  <label style={labelStyle}>Collision Elasticity: {controls.collisionElasticity.toFixed(2)}</label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={controls.collisionElasticity}
                    onChange={(e) => handleSimulationControl('collisionElasticity', parseFloat(e.target.value))}
                    style={sliderStyle}
                  />
                </div>

                <div style={controlGroupStyle}>
                  <label style={labelStyle}>Particle Speed: {controls.particleSpeed.toFixed(1)}</label>
                  <input
                    type="range"
                    min="0"
                    max="10"
                    step="0.5"
                    value={controls.particleSpeed}
                    onChange={(e) => handleSimulationControl('particleSpeed', parseFloat(e.target.value))}
                    style={sliderStyle}
                  />
                </div>

                <div style={controlGroupStyle}>
                  <label style={labelStyle}>Particle Scale: {controls.particleScale.toFixed(2)}</label>
                  <input
                    type="range"
                    min="0.1"
                    max="2"
                    step="0.1"
                    value={controls.particleScale}
                    onChange={(e) => handleSimulationControl('particleScale', parseFloat(e.target.value))}
                    style={sliderStyle}
                  />
                </div>

                <div style={controlGroupStyle}>
                  <label style={checkboxLabelStyle}>
                    <input
                      type="checkbox"
                      checked={controls.is2DMode}
                      onChange={(e) => handleSimulationControl('is2DMode', e.target.checked)}
                      style={checkboxStyle}
                    />
                    2D Mode
                  </label>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'view' && (
            <div style={tabContentStyle}>
              <div style={sectionStyle}>
                <h4 style={sectionTitleStyle}>View Settings</h4>
                
                <div style={controlGroupStyle}>
                  <label style={labelStyle}>Camera FOV: {cameraFov}°</label>
                  <input
                    type="range"
                    min="30"
                    max="120"
                    step="5"
                    value={cameraFov}
                    onChange={(e) => handleViewControl('cameraFov', parseInt(e.target.value))}
                    style={sliderStyle}
                  />
                </div>

                <div style={controlGroupStyle}>
                  <label style={labelStyle}>Background Color</label>
                  <input
                    type="color"
                    value={backgroundColor}
                    onChange={(e) => handleViewControl('backgroundColor', e.target.value)}
                    style={colorInputStyle}
                  />
                </div>

                <div style={controlGroupStyle}>
                  <label style={labelStyle}>Ambient Light: {ambientLightIntensity.toFixed(2)}</label>
                  <input
                    type="range"
                    min="0"
                    max="2"
                    step="0.1"
                    value={ambientLightIntensity}
                    onChange={(e) => handleViewControl('ambientLightIntensity', parseFloat(e.target.value))}
                    style={sliderStyle}
                  />
                </div>

                <div style={controlGroupStyle}>
                  <label style={labelStyle}>Viewport Scale: {viewportScaleFactor.toFixed(1)}</label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    step="0.1"
                    value={viewportScaleFactor}
                    onChange={(e) => handleViewControl('viewportScaleFactor', parseFloat(e.target.value))}
                    style={sliderStyle}
                  />
                </div>

                <div style={controlGroupStyle}>
                  <label style={labelStyle}>Shader</label>
                  <select
                    value={activeVisShader}
                    onChange={(e) => handleViewControl('activeVisShader', e.target.value)}
                    style={selectStyle}
                  >
                    <option value="PBR_InstanceColor">PBR Instance Color</option>
                    <option value="SwirlEffect">Swirl Effect</option>
                    <option value="TestBlue">Test Blue</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'style' && (
            <div style={tabContentStyle}>
              <div style={sectionStyle}>
                <h4 style={sectionTitleStyle}>Material</h4>
                
                <div style={controlGroupStyle}>
                  <label style={labelStyle}>Metalness: {metalness.toFixed(2)}</label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={metalness}
                    onChange={(e) => handleStyleControl('metalness', parseFloat(e.target.value))}
                    style={sliderStyle}
                  />
                </div>

                <div style={controlGroupStyle}>
                  <label style={labelStyle}>Roughness: {roughness.toFixed(2)}</label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={roughness}
                    onChange={(e) => handleStyleControl('roughness', parseFloat(e.target.value))}
                    style={sliderStyle}
                  />
                </div>

                <div style={controlGroupStyle}>
                  <label style={labelStyle}>Emissive Intensity: {emissiveIntensity.toFixed(2)}</label>
                  <input
                    type="range"
                    min="0"
                    max="2"
                    step="0.1"
                    value={emissiveIntensity}
                    onChange={(e) => handleStyleControl('emissiveIntensity', parseFloat(e.target.value))}
                    style={sliderStyle}
                  />
                </div>
              </div>

              <div style={sectionStyle}>
                <h4 style={sectionTitleStyle}>Bloom</h4>
                
                <div style={controlGroupStyle}>
                  <label style={labelStyle}>Intensity: {bloomIntensity.toFixed(2)}</label>
                  <input
                    type="range"
                    min="0"
                    max="3"
                    step="0.1"
                    value={bloomIntensity}
                    onChange={(e) => handleStyleControl('bloomIntensity', parseFloat(e.target.value))}
                    style={sliderStyle}
                  />
                </div>

                <div style={controlGroupStyle}>
                  <label style={labelStyle}>Threshold: {bloomThreshold.toFixed(2)}</label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={bloomThreshold}
                    onChange={(e) => handleStyleControl('bloomThreshold', parseFloat(e.target.value))}
                    style={sliderStyle}
                  />
                </div>

                <div style={controlGroupStyle}>
                  <label style={labelStyle}>Radius: {bloomRadius.toFixed(2)}</label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={bloomRadius}
                    onChange={(e) => handleStyleControl('bloomRadius', parseFloat(e.target.value))}
                    style={sliderStyle}
                  />
                </div>
              </div>

              <div style={sectionStyle}>
                <h4 style={sectionTitleStyle}>Box Style</h4>
                
                <div style={controlGroupStyle}>
                  <label style={labelStyle}>Style</label>
                  <select
                    value={boxStyle}
                    onChange={(e) => handleStyleControl('boxStyle', e.target.value)}
                    style={selectStyle}
                  >
                    <option value="normal">Normal</option>
                    <option value="hidden">Hidden</option>
                    <option value="glass">Glass</option>
                    <option value="forcefield">Forcefield</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'chemistry' && (
            <div style={tabContentStyle}>
              <div style={sectionStyle}>
                <h4 style={sectionTitleStyle}>Chemistry Presets</h4>
                
                <div style={controlGroupStyle}>
                  <label style={labelStyle}>Load Preset</label>
                  <select
                    onChange={(e) => handlePresetChange(e.target.value)}
                    style={selectStyle}
                    defaultValue=""
                  >
                    <option value="">-- Select Preset --</option>
                    {availablePresets.map(preset => (
                      <option key={preset.file} value={preset.file}>
                        {preset.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={controlGroupStyle}>
                  <button
                    style={actionButtonStyle}
                    onClick={resetSimulation}
                    disabled={!isChemistryConfigReady}
                  >
                    Reset Simulation
                  </button>
                </div>

                <div style={statusStyle}>
                  <span style={statusTextStyle}>
                    Status: {isChemistryConfigReady ? '✅ Ready' : '⏳ Loading...'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Styles
const containerStyle: React.CSSProperties = {
  position: 'absolute',
  top: '10px',
  right: '10px',
  width: 'auto',
  minWidth: '300px',
  maxWidth: '40vw',
  maxHeight: '600px',
  backgroundColor: 'rgba(40, 40, 40, 0.9)',
  color: '#eee',
  border: '1px solid #555',
  borderRadius: '4px',
  fontSize: '12px',
  fontFamily: 'monospace',
  zIndex: 1100,
  display: 'flex',
  flexDirection: 'column',
};

const headerStyle: React.CSSProperties = {
  padding: '8px 12px',
  backgroundColor: 'rgba(60, 60, 60, 0.9)',
  fontWeight: 'bold',
  flexShrink: 0,
  borderBottom: '1px solid #555',
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const buttonStyle: React.CSSProperties = {
  background: 'none',
  border: '1px solid #888',
  color: '#eee',
  cursor: 'pointer',
  padding: '2px 6px',
  fontSize: '10px',
  lineHeight: '1',
};

const contentStyle: React.CSSProperties = {
  overflowY: 'auto',
  padding: '8px',
  flexGrow: 1,
  minHeight: 0,
};

const tabContainerStyle: React.CSSProperties = {
  display: 'flex',
  gap: '4px',
  marginBottom: '8px',
  borderBottom: '1px solid #444',
  paddingBottom: '4px',
};

const tabButtonStyle: React.CSSProperties = {
  background: 'rgba(60, 60, 60, 0.8)',
  border: '1px solid #666',
  color: '#ccc',
  cursor: 'pointer',
  padding: '4px 8px',
  fontSize: '11px',
  borderRadius: '2px',
};

const activeTabStyle: React.CSSProperties = {
  background: 'rgba(0, 150, 255, 0.8)',
  border: '1px solid #0088ff',
  color: '#fff',
};

const tabContentStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
};

const sectionStyle: React.CSSProperties = {
  marginBottom: '16px',
  padding: '8px',
  backgroundColor: 'rgba(30, 30, 30, 0.8)',
  borderRadius: '4px',
  border: '1px solid #444',
};

const sectionTitleStyle: React.CSSProperties = {
  margin: '0 0 8px 0',
  fontSize: '13px',
  fontWeight: 'bold',
  color: '#4fc3f7',
};

const controlGroupStyle: React.CSSProperties = {
  marginBottom: '12px',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
};

const labelStyle: React.CSSProperties = {
  fontSize: '11px',
  color: '#ccc',
  fontWeight: 'bold',
};

const checkboxLabelStyle: React.CSSProperties = {
  fontSize: '11px',
  color: '#ccc',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  cursor: 'pointer',
};

const checkboxStyle: React.CSSProperties = {
  margin: 0,
  transform: 'scale(1.1)',
};

const sliderStyle: React.CSSProperties = {
  width: '100%',
  height: '4px',
  background: 'rgba(60, 60, 60, 0.8)',
  outline: 'none',
  borderRadius: '2px',
  cursor: 'pointer',
};

const colorInputStyle: React.CSSProperties = {
  width: '100%',
  height: '30px',
  border: '1px solid #666',
  borderRadius: '4px',
  cursor: 'pointer',
};

const selectStyle: React.CSSProperties = {
  width: '100%',
  padding: '4px 8px',
  backgroundColor: 'rgba(20, 20, 20, 0.8)',
  border: '1px solid #666',
  borderRadius: '4px',
  color: '#eee',
  fontSize: '11px',
};

const actionButtonStyle: React.CSSProperties = {
  background: 'rgba(60, 150, 60, 0.8)',
  border: '1px solid #44aa44',
  color: '#fff',
  cursor: 'pointer',
  padding: '6px 12px',
  fontSize: '11px',
  borderRadius: '4px',
  fontWeight: 'bold',
};

const startButtonStyle: React.CSSProperties = {
  background: 'rgba(60, 150, 60, 0.8)',
  border: '1px solid #44aa44',
};

const pauseButtonStyle: React.CSSProperties = {
  background: 'rgba(255, 150, 0, 0.8)',
  border: '1px solid #ff8800',
};

const resetButtonStyle: React.CSSProperties = {
  background: 'rgba(200, 60, 60, 0.8)',
  border: '1px solid #aa4444',
  color: '#fff',
  cursor: 'pointer',
  padding: '6px 12px',
  fontSize: '11px',
  borderRadius: '4px',
  fontWeight: 'bold',
};

const statusStyle: React.CSSProperties = {
  marginTop: '8px',
  padding: '4px 8px',
  backgroundColor: 'rgba(20, 20, 20, 0.8)',
  borderRadius: '4px',
  border: '1px solid #333',
};

const statusTextStyle: React.CSSProperties = {
  fontSize: '10px',
  color: '#aaa',
};

export default ControlPanel;
